import '../PlanSelection.scss';

import { Capacitor } from '@capacitor/core';
import { CMSPriceMatchBanner } from '@travelwin/cms/types';
import {
  Country,
  isDefaultBrand,
  ProductOffering,
  useConfig,
} from '@travelwin/core';
import { Fragment, useEffect, useRef, useState } from 'react';

import { ACTION_VIA_SOURCE_TYPE, AddToCartEventData } from '@/analytics';
import ClickablePlanCardContainer from '@/components/PlanCard/ClickablePlanCardContainer';
import { useMediaQuery } from '@/hooks';

import PriceMatchBanner from './PriceMatchBanner';

const className = 'p-PlanSelection';

export interface ProductClickContext {
  product: ProductOffering;
  countryName?: string;
  itemIndex?: number;
  actionViaSource?: ACTION_VIA_SOURCE_TYPE;
  baseItemId?: string;
}

export interface ProductCheckoutContext extends ProductClickContext {
  checkoutButtonType?: AddToCartEventData['checkoutButtonType'];
}

interface PlanCardListProps {
  list: ProductOffering[];
  currentCountry: Country;
  actionViaSource?: ACTION_VIA_SOURCE_TYPE;
  priceMatchBanner?: CMSPriceMatchBanner | null;
}

const PlanCardList = ({
  list,
  currentCountry,
  actionViaSource,
  priceMatchBanner,
}: PlanCardListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemsPerRow, setItemsPerRow] = useState(0);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { WHITELABEL_BRAND } = useConfig();
  const isNativePlatform = Capacitor.isNativePlatform();

  const shouldAddPriceMatchBanner = (index: number): boolean => {
    if (
      !isDefaultBrand(WHITELABEL_BRAND) ||
      !priceMatchBanner ||
      isNativePlatform
    ) {
      return false;
    }

    const isMobileCondition = isMobile && index === 1;
    const isDesktopCondition = !isMobile && index === itemsPerRow - 1;
    const isSingleItemCondition = list.length === 1;

    return isMobileCondition || isDesktopCondition || isSingleItemCondition;
  };

  useEffect(() => {
    const calcItemsPerRow = () => {
      if (!containerRef.current) return;

      if (list.length < 4) {
        setItemsPerRow(list.length);
        return;
      }

      const containerWidth = containerRef.current?.clientWidth || 0;
      const cardWidth = 280; // --plan-card-width: 280px;
      const numberOfCardsWithoutGap = Math.floor(containerWidth / cardWidth);

      const totalGap = 24 * (numberOfCardsWithoutGap - 1); //gap: 24px;
      const numberOfCardsWithGap = Math.floor(
        (containerWidth - totalGap) / cardWidth,
      );

      setItemsPerRow(numberOfCardsWithGap);
    };

    calcItemsPerRow();

    const handleResize = () => calcItemsPerRow();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [list]);

  return (
    <div ref={containerRef} className={`${className}__plans-container`}>
      {list?.map((product, index) => {
        return (
          <Fragment key={product.id}>
            <ClickablePlanCardContainer
              planDetails={product}
              actionViaSource={actionViaSource}
              itemIndex={index}
              countryName={currentCountry.english_name}
            />
            {priceMatchBanner && shouldAddPriceMatchBanner(index) && (
              <div className={`${className}__plans-container__promotion-block`}>
                <PriceMatchBanner priceMatchBanner={priceMatchBanner} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default PlanCardList;
